import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const Footer = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    gender: '',
    queries: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/contact`, formData);
      toast({
        title: "Got it! 🎉",
        description: "We'll get back to you within 24 hours. Thanks for reaching out!",
      });
    } catch (err) {
      console.error('Form submission error:', err);
      toast({
        title: "Oops! Something went wrong 😕",
        description: "Please try again or reach out to us directly.",
        variant: "destructive",
      });
    }
    setFormData({ 
      name: '', 
      email: '', 
      age: '', 
      gender: '',
      queries: ''
    });
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/newsletter`, { email: newsletterEmail });
      toast({
        title: "You're in! 💕",
        description: "Welcome! Expect cute gift ideas & exclusive offers soon.",
      });
    } catch (err) {
      if (err.response?.status === 409) {
        toast({
          title: "Already subscribed! 😊",
          description: "You're already on our list. We'll keep you posted!",
        });
      } else {
        toast({
          title: "Oops! Something went wrong 😕",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    }
    setNewsletterEmail('');
  };

  return (
    <footer className="footer-section" id="contact">
      <div className="footer-container">
        {/* Contact Form Section */}
        <div className="contact-form-section-simple">
          <div className="contact-form-header">
            <h3 className="contact-form-heading">Let's Chat! ✨</h3>
            <p className="contact-form-subheading">
              Got questions or want to get started? Fill this quick form and we'll be in touch!
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="contact-form-simple">
            <div className="form-row">
              <div className="form-field">
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <Input
                  type="email"
                  name="email"
                  placeholder="Your Email *"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <Input
                  type="number"
                  name="age"
                  placeholder="Age"
                  value={formData.age}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <Input
                  type="text"
                  name="gender"
                  placeholder="Gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-field">
              <Textarea
                name="queries"
                placeholder="Any queries? Tell us what you're looking for... 💬"
                value={formData.queries}
                onChange={handleInputChange}
                className="form-textarea"
                rows={4}
              />
            </div>

            <Button type="submit" className="form-submit-button">
              <Send className="w-5 h-5 mr-2" />
              Send Message
            </Button>
          </form>
        </div>

        {/* Footer Bottom Section */}
        <div className="footer-top">
          <div className="footer-column brand-column">
            <div className="footer-logo">
              <div className="logo-icon">🎁</div>
              <span className="logo-text">GiftEmotion</span>
            </div>
            <p className="footer-tagline">
              Making your gifts look special is what we love. We're all about that aesthetic life and 
              turning ordinary into extraordinary, one package at a time 💝✨
            </p>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links">
              <li><a href="#collections">Our Themes</a></li>
              <li><a href="#about">How It Works</a></li>
              <li><a href="#contact">Get Started</a></li>
            </ul>
          </div>

          <div className="footer-column newsletter-column">
            <h3 className="footer-heading">Stay Updated</h3>
            <p className="newsletter-text">
              Get the latest on new themes & exclusive deals ✨
            </p>
            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
              <Input
                type="email"
                placeholder="your@email.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                className="newsletter-input"
              />
              <Button type="submit" className="newsletter-button">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="copyright">
            © 2026 GiftEmotion. All rights reserved. Made with 💕
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
